import { Link } from 'react-router-dom'

const guide = {
  slug: 'porn-blocker-you-cant-uninstall',
  category: 'holds',
  kicker: 'The mechanism',
  title: 'A porn blocker you can’t uninstall: what actually makes one immovable',
  seoTitle: 'Porn Blocker You Can’t Uninstall (2026): How It Works',
  description: `Most “uninstall-proof” blockers are a password you know. The four levels a porn blocker can sit at, and why only a supervised profile holds at 2am.`,
  published: '2026-08-25',
  modified: '2026-09-03',
  minutes: 7,
  intro: `"Can't be uninstalled" is the most-used and least-true sentence in
          this category. Almost every blocker that says it means "there's a
          password" — and you set the password. This piece explains, level
          by level, what keeps a blocker on a device, so you can tell a lock
          from a sticky note before you pay for one.`,
  next: ['how-to-block-porn-on-iphone', 'does-incognito-bypass-porn-blockers', 'how-to-quit-porn'],
  body: (
    <>
      <h2>Why "uninstallable" is the only question</h2>
      <p>
        A blocker has one job: still be there at the hour you'd remove it.
        Any filter can stop you on a Tuesday afternoon. The relapse doesn't
        happen on a Tuesday afternoon. It happens late, tired, alone, with a
        brain on autopilot — and in that state, a block you can take off
        yourself in thirty seconds is not a block. It's a door you've
        already opened a hundred times. So before features, before price,
        before reviews, one question: <b>if I crack at 2am, can I turn it
        off?</b>
      </p>

      <h2>Level 0 — An app you can delete</h2>
      <p>
        A blocker that's just an app is removed like any app: hold the icon,
        tap the cross. Some add a "delay" or an "are you sure" screen; on
        iPhone the app can be deleted from the home screen regardless of
        what it shows inside. This level is a reminder, not a restriction.
      </p>

      <h2>Level 1 — A password or passcode</h2>
      <p>
        Screen Time, most parental-control apps, and most "uninstall
        protection" features live here. Turning the block off requires a
        code. Two problems. The obvious one: <b>you set the code</b>. The
        less obvious one: even if someone else did, the platforms provide
        recovery — a Screen Time passcode can be reset with the Apple ID on
        the phone, and that Apple ID is yours. A password is a lock whose
        key is in your own pocket.
      </p>

      <h2>Level 2 — A profile or a system-level filter</h2>
      <p>
        Here the block leaves the app and enters the operating system: a
        configuration profile that sets an encrypted, category-filtering DNS
        resolver, so that every app and every browser on the device —
        private mode included — gets the same refusal. This is the right
        <em> filter</em>: it doesn't care which browser you use and it
        doesn't need a list of sites.
      </p>
      <p>
        But an ordinary profile is removed in <b>Settings → General → VPN
        &amp; Device Management → Remove Profile</b>. Four taps. Nobody
        asks for a password. The filter is finally right; the attachment is
        still a sticky note.
      </p>

      <h2>Level 3 — A supervised device</h2>
      <p>
        Supervision is an Apple mechanism built for organisations that hand
        out iPhones and iPads and need settings that stick. On a supervised
        device, a profile can be marked non-removable: the Remove Profile
        button doesn't exist. The same profile can forbid configuring a VPN,
        which closes the usual back door. Nothing on the phone can undo it,
        and there is no password to guess because there is no password.
      </p>
      <p>
        This is the level SELFCNTRL works at on iPhone and iPad. The filter
        is the level-2 category filter; supervision is what makes it stay.
        The only way off is a <b>deliberate request from your account</b>,
        which takes effect after a cooling-off delay. You remain free to
        stop. You are simply no longer able to stop on an impulse — which is
        the only kind of stopping you were trying to prevent.
      </p>

      <h2>The four levels in one table</h2>
      <table>
        <thead>
          <tr><th>Level</th><th>Example</th><th>How it comes off</th><th>Holds at 2am?</th></tr>
        </thead>
        <tbody>
          <tr><td>0 · App</td><td>Any blocker app</td><td>Delete the app</td><td>No</td></tr>
          <tr><td>1 · Passcode</td><td>Screen Time, "uninstall protection"</td><td>Enter or reset the code</td><td>No</td></tr>
          <tr><td>2 · Profile / DNS</td><td>A DNS profile you installed</td><td>Remove Profile, four taps</td><td>No</td></tr>
          <tr><td>3 · Supervised</td><td>SELFCNTRL on iPhone / iPad</td><td>Request from account + delay</td><td>Yes</td></tr>
        </tbody>
      </table>

      <h2>The other half: a lock that only tightens</h2>
      <p>
        Being unable to remove the filter is half the design. The other half
        is what you can change inside it. In SELFCNTRL, <b>tightening takes
        effect immediately</b>: add a locked day, extend a curfew, block
        another category, and it's done. <b>Loosening waits twenty-four
        hours</b>. And the floor — the number of locked days you set at the
        start — never drops below where you put it. An urge to loosen has
        to survive a full night to succeed, and urges don't.
      </p>
      <p>
        That's why the pace is chosen once, on a good day: cut it dead (seven
        days a week from the first minute), or cut it in steps (four locked
        days, then one more every two weeks until it's seven out of seven).
        The you of this morning decides for the you of tonight.
      </p>

      <h2>Why we put $100 on it</h2>
      <p>
        Every blocker says it can't be beaten. We'd rather stand behind it
        with money: on a device we locked, if you strip the filter off by
        your own means, we send you $100 on top of a full refund. It isn't
        bravado — it's what supervision makes possible. Conditions are in
        the <Link to="/remboursement">refund policy</Link>.
      </p>

      <h2>What "immovable" does not mean</h2>
      <p>
        It doesn't mean surveillance. The block restricts; it doesn't look.
        No history is read or stored, because category filtering has none to
        keep. Nobody is told. There's no remote wipe, no location, no app
        installed for you. And it doesn't mean forever: abandoning the
        profile from your account releases the device once the delay has
        run. Slow on purpose. Never impossible.
      </p>
      <p>
        If you're on iPhone, the ten-minute setup is in
        <Link to="/guides/how-to-block-porn-on-iphone"> the iPhone guide</Link>.
        If you're wondering whether it's even the right approach for you,
        start with <Link to="/guides/how-to-quit-porn">how to quit porn</Link>.
      </p>
    </>
  ),
  faq: [
    {
      q: 'Is there a porn blocker that really can’t be uninstalled on iPhone?',
      a: `Only at the supervised level. An app, a passcode or an ordinary
          profile can all be removed from the phone by the person holding it.
          A profile on a supervised iPhone cannot; that's the mechanism
          SELFCNTRL uses, and the one we put $100 behind.`
    },
    {
      q: 'What if I genuinely need to remove it one day?',
      a: `You request it from your account and it takes effect after a
          cooling-off delay. You're never trapped — you're just unable to do
          it in thirty seconds at two in the morning.`
    },
    {
      q: 'Does supervision give you control of my phone?',
      a: `No. It's used for one thing: keeping the filter profile on. No
          remote commands, no wiping, no location, no reading anything on the
          device. It's written into the privacy policy.`
    }
  ]
}

export default guide
